import React from "react";
import PropTypes from "prop-types";
import styles from "components/documents/custom-tab-filters.scss";
import FaSearch from "react-icons/fa/search";
import {
  cardNumberType,
  categoryType,
  currencyType,
  envelopeType,
  momentType,
  documentFiltersType,
  vendorType
} from "custom-prop-types";
import { onEnterPressed } from "utils/key-press";
import { filterCounter } from "utils/filter-counter";
import { isValidValue } from "utils/validators";
import { Select, SimpleSelect } from "components/common/select";
import { Button } from "components/common/button";
import { DateRange } from "components/common/date-range";
import { PaymentTypeFilterField } from "components/documents/filters/payment-type-filter-field";
import { ExportTypeSelector } from "components/documents/filters/export-type-selector";
import { DocumentTypeSelector } from "components/documents/filters/document-type-selector";

export const CustomTabFilters = ({
  accountId,
  cardNumbers,
  categories,
  currencies,
  customTabName,
  endDate,
  envelopes,
  filters,
  isCreateCustomTabLoading,
  lastFourDigits,
  onAddCardNumber,
  onAddCategory,
  onAddCurrency,
  onAddDocumentType,
  onAddEndDate,
  onAddEnvelope,
  onAddSearchQuery,
  onAddPaymentType,
  onAddStartDate,
  onAddVendor,
  onAddViewedStatus,
  onCreateCustomTab,
  onSetExportType,
  onUpdateCustomTabName,
  onUpdateSearchQuery,
  preferredCurrency,
  searchQueryString,
  selectedPaymentTypes,
  startDate,
  vendors
}) => (
  <div className={ styles.container }>
    <div className={ styles.action }>
      <Button
        disabled={ filterCounter(filters) === 0 || !isValidValue(customTabName) }
        loading={ isCreateCustomTabLoading }
        onClick={ () => onCreateCustomTab(accountId, filters, customTabName) }
        primary
      >
        Create tab
      </Button>
    </div>
    <h3>Create a custom documents tab!</h3>
    <h4>Choose a name for your custom tab:</h4>
    <div className={ styles["field-cell"] }>
      <input
        className={ styles["input-field"] }
        onChange={ (e) => onUpdateCustomTabName(e.target.value) }
        placeholder="E.g. Other Documents"
        value={ customTabName }
      />
    </div>
    <h4>Choose the default filters for your custom tab:</h4>
    <div className={ styles["field-cell"] }>
      <DocumentTypeSelector onChange={ onAddDocumentType } />
    </div>
    <div className={ styles["field-cell"] }>
      <span>Vendor</span>
      <Select data={ mapVendors(vendors) } onChange={ onAddVendor } placeholder="Select a vendor..." />
    </div>
    <div className={ styles["field-cell"] }>
      <span>Category</span>
      <Select data={ mapCategories(categories) } onChange={ onAddCategory } placeholder="Select a category..." />
    </div>
    <div className={ styles["field-cell"] }>
      <span>Date Range</span>
      <DateRange
        currency={ preferredCurrency }
        endDate={ endDate }
        onAddEndDate={ onAddEndDate }
        onAddStartDate={ onAddStartDate }
        startDate={ startDate }
      />
    </div>
    <div className={ styles["field-cell"] }>
      <span>Envelope</span>
      <Select
        data={ mapEnvelopes(envelopes) }
        onChange={ onAddEnvelope }
        placeholder="Select an envelope..."
      />
    </div>
    <div className={ styles["field-cell"] }>
      <span>Currency</span>
      <SimpleSelect
        data={ mapCurrencies(currencies) }
        onChange={ onAddCurrency }
        placeholder="Select a currency..."
      />
    </div>
    <div className={ styles["field-cell"] }>
      <span>Viewed</span>
      <SimpleSelect
        data={ viewedArray }
        onChange={ onAddViewedStatus }
        placeholder="Select viewed status..."
      />
    </div>
    <div className={ styles["field-cell"] }>
      <span>Search</span>
      <div className={ styles.search }>
        <input
          className={ styles["search-field"] }
          onChange={ (e) => onUpdateSearchQuery(e.target.value) }
          onKeyDown={ (e) => onEnterPressed(e, searchQueryString, onAddSearchQuery) }
          placeholder={ "Search for documents here" }
          value={ searchQueryString }
        />
        <FaSearch className={ styles["search-button"] } />
      </div>
    </div>

    <div className={ styles["field-cell"] }>
      <span>Export Type</span>
      <ExportTypeSelector categories={ categories } onChange={ onSetExportType } />
    </div>
    <div className={ `${styles["field-cell"]} ${styles["payment-type-container"]}` }>
      <div className={ styles["payment-type"] }>
        <PaymentTypeFilterField
          cardNumbers={ cardNumbers }
          lastFourDigits={ lastFourDigits }
          onAddCardNumber={ onAddCardNumber }
          onAddPaymentType={ onAddPaymentType }
          selectedPaymentTypes={ selectedPaymentTypes }
        />
      </div>
    </div>
  </div>
);

CustomTabFilters.propTypes = {
  accountId: PropTypes.string.isRequired,
  cardNumbers: PropTypes.arrayOf(cardNumberType).isRequired,
  categories: PropTypes.arrayOf(categoryType).isRequired,
  currencies: PropTypes.arrayOf(currencyType).isRequired,
  customTabName: PropTypes.string.isRequired,
  endDate: momentType,
  envelopes: PropTypes.arrayOf(envelopeType).isRequired,
  filters: documentFiltersType.isRequired,
  isCreateCustomTabLoading: PropTypes.bool.isRequired,
  lastFourDigits: PropTypes.string,
  onAddCardNumber: PropTypes.func.isRequired, // (string) => void
  onAddCategory: PropTypes.func.isRequired, // ({ key: string, name: string }) => void
  onAddCurrency: PropTypes.func.isRequired, // ({ key: string, name: string }) => void
  onAddDocumentType: PropTypes.func.isRequired,
  onAddEndDate: PropTypes.func.isRequired,
  onAddEnvelope: PropTypes.func.isRequired, // ({ key: string, name: string }) => void
  onAddPaymentType: PropTypes.func.isRequired, // ({ key: string, name: string }) => void
  onAddSearchQuery: PropTypes.func.isRequired,
  onAddStartDate: PropTypes.func.isRequired,
  onAddVendor: PropTypes.func.isRequired, // ({key: string, name: string}) => void
  onAddViewedStatus: PropTypes.func.isRequired,
  onCreateCustomTab: PropTypes.func.isRequired,
  onSetExportType: PropTypes.func.isRequired, // (string) => void
  onUpdateCustomTabName: PropTypes.func.isRequired,
  onUpdateSearchQuery: PropTypes.func.isRequired,
  preferredCurrency: PropTypes.string.isRequired,
  searchQueryString: PropTypes.string.isRequired,
  selectedPaymentTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  startDate: momentType,
  vendors: PropTypes.arrayOf(vendorType).isRequired
};

CustomTabFilters.defaultProps = {
  endDate: null,
  exportType: null,
  lastFourDigits: null,
  selectedSearchQueryString: null,
  startDate: null,
  type: null
};

const mapEnvelopes = (envelopes) => envelopes.map(envelope => ({ key: envelope.code, name: envelope.code }));
const mapCategories = (categories) => categories.map(category => ({ key: category.name, name: category.name }));
const mapCurrencies = (currencies) => currencies.map(currency => ({ key: currency.code, name: currency.code }));
const mapVendors = (vendors) => vendors.map(vendor => ({ key: vendor.name, name: vendor.name }));

const viewedArray = [
  {
    key: "viewed",
    name: "Viewed"
  },
  {
    key: "unviewed",
    name: "Unviewed"
  }
];



// WEBPACK FOOTER //
// ./src/js/components/documents/custom-tab-filters.jsx